import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import axios from "axios";
import { Mail, RefreshCw, Check, X, Inbox, Clock, History } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = BACKEND_URL + "/api";

function ResultRow(props) {
  var r = props.item;
  var idx = props.idx;
  var isNew = r.action === "created";
  var isErr = r.action === "error";
  var isSkipped = r.action === "skipped";
  var bg = isErr ? "bg-red-950/20 border border-red-900" : isSkipped ? "bg-yellow-950/20 border border-yellow-900/50" : "bg-secondary/30";
  var dot = isNew ? "bg-green-400" : isSkipped ? "bg-yellow-400" : isErr ? "bg-red-400" : "bg-blue-400";
  var name = String(r.project_name || r.subject || "Unknown");
  var desc = isNew ? "New project created" : r.action === "updated" ? "Added to existing project" : isSkipped ? "Skipped (not a graphics request)" : String(r.error || "");
  var link = r.project_id ? "/projects/" + r.project_id : null;

  return (
    <div key={idx} className={"flex items-center gap-3 p-3 rounded-lg " + bg} data-testid={"result-" + idx}>
      <div className={"w-2 h-2 rounded-full flex-shrink-0 " + dot} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <p className="text-sm font-medium truncate">{name}</p>
          {r.ai_parsed && <span className="text-[10px] px-1.5 py-0.5 bg-purple-950/50 text-purple-300 rounded border border-purple-800/50">AI</span>}
          {r.customer_matched && <span className="text-[10px] px-1.5 py-0.5 bg-emerald-950/50 text-emerald-300 rounded border border-emerald-800/50">Customer matched</span>}
        </div>
        <p className="text-xs text-muted-foreground">{desc}</p>
      </div>
      {link && <a href={link} className="text-xs text-blue-400 hover:underline">View</a>}
    </div>
  );
}

function HistoryRow(props) {
  var log = props.item;
  var dateStr = new Date(log.completed_at).toLocaleString();

  return (
    <div className="flex items-center gap-4 py-3 border-b border-border last:border-0">
      <Mail size={16} className="text-muted-foreground flex-shrink-0" />
      <span className="text-sm w-20">{log.total_emails} emails</span>
      <span className="text-xs px-2 py-0.5 bg-green-950/50 text-green-400 rounded">+{log.created}</span>
      <span className="text-xs px-2 py-0.5 bg-blue-950/50 text-blue-400 rounded">~{log.updated}</span>
      {log.skipped > 0 && <span className="text-xs px-2 py-0.5 bg-yellow-950/50 text-yellow-400 rounded">-{log.skipped}</span>}
      <span className="text-xs text-muted-foreground ml-auto">{dateStr}</span>
    </div>
  );
}

function ResultsList(props) {
  var items = props.items;
  var children = [];
  for (var i = 0; i < items.length; i++) {
    children.push(<ResultRow key={i} item={items[i]} idx={i} />);
  }
  return <div className="space-y-2 max-h-96 overflow-y-auto">{children}</div>;
}

function HistoryList(props) {
  var items = props.items;
  var children = [];
  for (var i = 0; i < items.length; i++) {
    children.push(<HistoryRow key={i} item={items[i]} />);
  }
  return <div>{children}</div>;
}

export const EmailInboxPage = () => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [polling, setPolling] = useState(false);
  const [pollResults, setPollResults] = useState(null);
  const [pollHistory, setPollHistory] = useState([]);

  const fetchStatus = useCallback(async () => {
    try {
      const res = await axios.get(API + "/email/status");
      setStatus(res.data);
    } catch (err) {
      setStatus({ connected: null, statusUnavailable: true, error: "Status service unavailable; mailbox connectivity not determined" });
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchHistory = useCallback(async () => {
    try {
      const res = await axios.get(API + "/email/poll-history");
      setPollHistory(res.data.logs || []);
    } catch (err) {
      // ignore
    }
  }, []);

  useEffect(() => {
    fetchStatus();
    fetchHistory();
  }, [fetchStatus, fetchHistory]);

  const handlePoll = async () => {
    setPolling(true);
    setPollResults(null);
    try {
      const res = await axios.post(API + "/email/poll");
      setPollResults(res.data);
      toast.success("Processed " + res.data.total_emails + " emails");
      fetchHistory();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to poll inbox");
    } finally {
      setPolling(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-pulse text-muted-foreground">Checking inbox connection...</div>
      </div>
    );
  }

  var isConnected = status !== null && status.connected === true;
  var statusUnavailable = !!(status && status.statusUnavailable);
  var jobs = status && status.jobs ? status.jobs : {};
  var jobNames = [
    ["legacy_email_poll", "Legacy intake poll"],
    ["project_email_comms_poll", "Project replies"],
    ["project_archive_maintenance", "Archive maintenance"],
  ];
  var mailbox = (status !== null && status.mailbox) ? status.mailbox : "graphics@gamerpackaging.com";
  var statusError = (status !== null && status.error) ? status.error : "Unable to access the shared mailbox";
  var hasLastPoll = status !== null && status.last_poll !== null && status.last_poll !== undefined;
  var lastPollDate = hasLastPoll ? new Date(status.last_poll.completed_at).toLocaleString() : "";
  var autoPoll = (status !== null && status.auto_poll) ? status.auto_poll : null;
  var hasResults = pollResults !== null;
  var totalEmails = hasResults ? pollResults.total_emails : 0;
  var createdCount = hasResults ? pollResults.created : 0;
  var updatedCount = hasResults ? pollResults.updated : 0;
  var skippedCount = hasResults ? (pollResults.skipped || 0) : 0;
  var resultItems = hasResults ? (pollResults.results || []) : [];

  return (
    <div className="space-y-6 animate-fade-in" data-testid="email-inbox-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-3">
            <Inbox size={28} />
            Graphics Email Inbox
          </h1>
          <p className="text-muted-foreground mt-1">
            Poll <strong>{mailbox}</strong> for new project requests
          </p>
        </div>
        <button
          data-testid="poll-inbox-btn"
          onClick={handlePoll}
          disabled={polling || !isConnected}
          className="flex items-center gap-2 px-5 py-2.5 bg-primary text-primary-foreground rounded-lg font-medium hover:bg-primary/90 disabled:opacity-50"
        >
          {polling ? <RefreshCw size={18} className="animate-spin" /> : <Mail size={18} />}
          {polling ? "Polling..." : "Check for New Emails"}
        </button>
      </div>

      <div className="bg-card border border-border rounded-xl p-6">
        <div className="flex items-center gap-4">
          <div className={"w-12 h-12 rounded-full flex items-center justify-center " + (isConnected ? "bg-green-950/50" : "bg-red-950/30")}>
            {isConnected ? <Check size={24} className="text-green-400" /> : <X size={24} className="text-red-400" />}
          </div>
          <div className="flex-1">
            <h2 className="text-lg font-semibold">
              {statusUnavailable ? "Status Unavailable" : isConnected ? "Mailbox Connected" : "Not Connected"}
            </h2>
            <p className="text-sm text-muted-foreground">
              {isConnected ? "Reading from " + mailbox : statusError}
            </p>
            {hasLastPoll && (
              <p className="text-xs text-muted-foreground mt-1">
                <Clock size={12} className="inline mr-1" />
                Last polled: {lastPollDate}
              </p>
            )}
          </div>
          {autoPoll && (
            <div className={"flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium " + (autoPoll.active ? "bg-green-950/50 text-green-400 border border-green-800/50" : "bg-secondary text-muted-foreground")}>
              <RefreshCw size={12} className={autoPoll.active ? "animate-spin-slow" : ""} />
              {autoPoll.active ? "Legacy intake enabled (" + autoPoll.interval_minutes + "min)" : "Legacy intake disabled"}
            </div>
          )}
        </div>
      </div>

      <div className="bg-card border border-border rounded-xl p-6">
        <h3 className="font-semibold mb-3">Background Job Health</h3>
        <p className="text-xs text-muted-foreground mb-4">Legacy poll history below is separate from project email processing. Job status is reset when the backend restarts.</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {jobNames.map(([key, title]) => {
            const job = jobs[key] || {};
            const lastSuccess = job.last_success_at ? new Date(job.last_success_at).toLocaleString() : "No success recorded since restart";
            const label = job.status || "unknown";
            return <div key={key} className="rounded-lg bg-secondary/30 p-3 border border-border">
              <p className="font-medium text-sm">{title}</p>
              <p className="text-sm mt-1">Status: {label}</p>
              <p className="text-xs text-muted-foreground mt-1">Last success: {lastSuccess}</p>
              <p className="text-xs text-muted-foreground">Runs: {job.run_count || 0} · Errors: {job.error_count || 0}</p>
              <p className="text-xs text-muted-foreground">Interval: {job.cadence_seconds ? Math.round(job.cadence_seconds / 60) + " min" : "Not reported"}</p>
            </div>;
          })}
        </div>
      </div>

      {hasResults && (
        <div className="bg-card border border-border rounded-xl p-6">
          <h3 className="font-semibold mb-4 flex items-center gap-2">
            <Mail size={18} />
            Poll Results
            <span className="ml-auto text-sm text-muted-foreground">{totalEmails} emails processed</span>
          </h3>
          {totalEmails === 0 ? (
            <div className="text-center py-6 text-muted-foreground">
              <Inbox size={36} className="mx-auto mb-2 opacity-50" />
              <p>No unread emails in the inbox</p>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="flex gap-4 mb-4">
                <span className="text-sm px-3 py-1 bg-green-950/50 text-green-400 rounded-lg">{createdCount} new projects</span>
                <span className="text-sm px-3 py-1 bg-blue-950/50 text-blue-400 rounded-lg">{updatedCount} updated</span>
                {skippedCount > 0 && <span className="text-sm px-3 py-1 bg-yellow-950/50 text-yellow-400 rounded-lg">{skippedCount} skipped</span>}
              </div>
              <ResultsList items={resultItems} />
            </div>
          )}
        </div>
      )}

      <div className="bg-secondary/30 border border-border rounded-xl p-6">
        <h3 className="font-semibold mb-3 flex items-center gap-2">
          How AI Email Intake Works
          <span className="text-[10px] px-1.5 py-0.5 bg-purple-950/50 text-purple-300 rounded border border-purple-800/50">Powered by Gemini AI</span>
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-sm text-muted-foreground">
          <div className="space-y-1">
            <p className="font-medium text-foreground">1. New Email</p>
            <p>Sales sends artwork requests to the graphics inbox.</p>
          </div>
          <div className="space-y-1">
            <p className="font-medium text-foreground">2. AI Parsing</p>
            <p>Gemini AI extracts customer, manufacturer, PO#, priority, and more from each email.</p>
          </div>
          <div className="space-y-1">
            <p className="font-medium text-foreground">3. Smart Matching</p>
            <p>AI-extracted names are matched to existing customers and manufacturers in the system.</p>
          </div>
          <div className="space-y-1">
            <p className="font-medium text-foreground">4. Auto-Create or Update</p>
            <p>New projects are created with enriched data, or emails attach to existing projects.</p>
          </div>
        </div>
      </div>

      {pollHistory.length > 0 && (
        <div className="bg-card border border-border rounded-xl p-6">
          <h3 className="font-semibold flex items-center gap-2 mb-4">
            <History size={18} />
            Poll History
          </h3>
          <HistoryList items={pollHistory} />
        </div>
      )}
    </div>
  );
};
